import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonSpinner,
} from '@ionic/react';
import {
  useCanMergePlots,
  useMergePlots,
} from '@lidea/shared/data-access/plots';
import { MergeAlert } from '@lidea/shared/ui/core';
import { PfSelect } from '@lidea/shared/ui/form';
import { ModalPage } from '@lidea/shared/ui/layout';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { chevronBackOutline, chevronForwardOutline } from 'ionicons/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ColorScale from '../color-scale/color-scale';
import {
  isSatelliteImageryIndex,
  SATELLITE_IMAGERY_INDEXES,
  SatelliteColorbar,
  SatellitePlotInfo,
} from '../satellite-map/satellite-map.types';
import styles from './satellite-map-modal.module.css';

export interface SatelliteMapModalProps {
  infoPlot: SatellitePlotInfo[];
  colorbar?: SatelliteColorbar;
  imageryIndex: string;
  setImageryIndex: (value: string) => void;
  selectedPlot: Record<number, boolean>;
  numberOfPlots: number;
  onCheckboxChange?: (id: number, checked: boolean) => void;
  dismiss: () => void;
  dateIndices: number[];
  onDateChange: (plotIndex: number, direction: 'prev' | 'next') => void;
  allPlots: boolean;
  setAllPlots: (value: boolean) => void;
  isLoading?: boolean;
}

export function SatelliteMapModal(props: SatelliteMapModalProps) {
  const { t } = useTranslation(['map', 'plot', 'common', 'analysis'], {
    useSuspense: false,
  });

  const [mergeDialogOpened, mergeDialogHandlers] = useDisclosure();
  const [selectedPlots, setSelectedPlots] = useState<{
    all: boolean;
    plotsIds: number[];
  }>({ all: false, plotsIds: [] });

  const canMergePlots = useCanMergePlots({
    plotsIds: selectedPlots.all ? [] : selectedPlots.plotsIds,
    all: selectedPlots.all,
  });

  const mergePlots = useMergePlots();
  const hasImageryIndex = isSatelliteImageryIndex(props.imageryIndex);
  const imageryOptions = useMemo(
    () =>
      SATELLITE_IMAGERY_INDEXES.map((value) => ({
        value,
        label: value === 'RGB' ? t('map:rgb_imagery') : value,
      })),
    [t]
  );
  const showMergeAction =
    (selectedPlots.all && props.numberOfPlots > 1) ||
    selectedPlots.plotsIds.length > 1;

  const resetSatelliteMap = () => {
    props.setImageryIndex('');
    props.setAllPlots(false);
    setSelectedPlots({ all: false, plotsIds: [] });
  };

  const handleSelectPlots = useCallback(
    (all: boolean, plotsIds: readonly number[]) => {
      setSelectedPlots({
        all,
        plotsIds: plotsIds as number[],
      });
    },
    []
  );

  const handleToggleAllPlots = (checked: boolean) => {
    props.setAllPlots(checked);
    handleSelectPlots(checked, []);
    props.infoPlot.forEach((plot) => {
      if (plot.checked !== checked) {
        props.onCheckboxChange?.(plot.plotId, checked);
      }
    });
  };

  const notifyMergeSuccess = () => {
    notifications.show({
      id: 'merge-plot',
      message: t('map:message_merge'),
      color: 'green',
      withCloseButton: true,
      loading: false,
      autoClose: true,
    });
  };

  const handleMergePlot = () => {
    mergePlots.mutate({
      plotsIds: selectedPlots.plotsIds,
      all: selectedPlots.all,
      forceMerge: true,
    });
    notifyMergeSuccess();
  };

  const handleClickMerge = () => {
    if (canMergePlots.data?.can_be_merged === 'yes_but') {
      mergeDialogHandlers.open();
      return;
    }

    mergePlots.mutate({
      plotsIds: selectedPlots.plotsIds,
      all: selectedPlots.all,
      forceMerge: true,
    });
    notifyMergeSuccess();
  };

  useEffect(() => {
    if (mergePlots.isSuccess) {
      setSelectedPlots({ all: false, plotsIds: [] });
    }
  }, [mergePlots.isSuccess]);

  useEffect(() => {
    const selectedIds = Object.keys(props.selectedPlot)
      .map(Number)
      .filter((id) => props.selectedPlot[id]);

    handleSelectPlots(
      selectedIds.length > 0 && selectedIds.length === props.numberOfPlots,
      selectedIds
    );
  }, [handleSelectPlots, props.numberOfPlots, props.selectedPlot]);

  return (
    <ModalPage
      title={t('plot:satellite_map')}
      toolbarEnd={
        showMergeAction ? (
          <IonButton
            className={styles.toolbarMerge}
            color="success"
            disabled={canMergePlots.data?.can_be_merged === 'no'}
            fill="solid"
            size="small"
            onClick={handleClickMerge}
          >
            {t('map:merge_short')}
          </IonButton>
        ) : undefined
      }
      toolbarStart={
        hasImageryIndex ? (
          <IonButton
            className={styles.toolbarReset}
            fill="clear"
            onClick={resetSatelliteMap}
          >
            {t('common:reset')}
          </IonButton>
        ) : undefined
      }
      onClose={props.dismiss}
    >
      <div className={styles.page}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>
            {t('analysis:image_indexes_placeholder')}
          </h2>
          <PfSelect
            ariaLabel={t('analysis:image_indexes_placeholder') as string}
            options={imageryOptions}
            placeholder={t('plot:select_satellite_map') as string}
            searchable={false}
            value={hasImageryIndex ? props.imageryIndex : undefined}
            wrapperClassName={styles.selectField}
            onChange={(value) => {
              props.setImageryIndex(value);
              props.setAllPlots(false);
              setSelectedPlots({ all: false, plotsIds: [] });
            }}
          />
          {!hasImageryIndex ? (
            <p className={styles.hint}>{t('map:select_index_hint')}</p>
          ) : null}
        </section>

        {props.isLoading ? (
          <div className={styles.loadingOverlay}>
            <IonSpinner name="crescent" />
            <span>{t('map:loading_imagery')}</span>
          </div>
        ) : null}

        {hasImageryIndex ? (
          <>
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t('map:visible_plots')}</h2>
              <IonList className={styles.plotList} lines="none">
                <IonItem className={styles.allPlotsItem} lines="none">
                  <IonCheckbox
                    checked={props.allPlots}
                    legacy
                    slot="start"
                    onIonChange={(event) =>
                      handleToggleAllPlots(event.detail.checked)
                    }
                  />
                  <IonLabel>{t('plot:all_plots')}</IonLabel>
                </IonItem>

                {props.infoPlot.map((plot, plotIndex) => {
                  const rasters = plot.rasters ?? [];
                  const hasRasters = rasters.length > 0;
                  const dateIndex = props.dateIndices[plotIndex] ?? 0;
                  const currentRaster = hasRasters ? rasters[dateIndex] : null;

                  return (
                    <IonItem key={plot.plotId} lines="none">
                      <IonCheckbox
                        checked={plot.checked}
                        labelPlacement="end"
                        legacy
                        slot="start"
                        onIonChange={(event) =>
                          props.onCheckboxChange?.(
                            plot.plotId,
                            event.detail.checked
                          )
                        }
                      />
                      <IonLabel>
                        <div
                          className={
                            hasRasters
                              ? styles.plotName
                              : `${styles.plotName} ${styles.plotNameUnavailable}`
                          }
                        >
                          {plot.plotName ?? '—'}
                        </div>

                        {!hasRasters ? (
                          <div className={styles.plotMeta}>
                            {t('map:message_no_image')}
                          </div>
                        ) : (
                          <>
                            <div className={styles.dateNav}>
                              <IonButton
                                aria-label={t('common:previous')}
                                disabled={dateIndex <= 0}
                                fill="clear"
                                size="small"
                                onClick={() =>
                                  props.onDateChange(plotIndex, 'prev')
                                }
                              >
                                <IonIcon icon={chevronBackOutline} />
                              </IonButton>
                              <span className={styles.dateLabel}>
                                {currentRaster?.date ?? '—'}
                              </span>
                              <IonButton
                                aria-label={t('common:next')}
                                disabled={dateIndex >= rasters.length - 1}
                                fill="clear"
                                size="small"
                                onClick={() =>
                                  props.onDateChange(plotIndex, 'next')
                                }
                              >
                                <IonIcon icon={chevronForwardOutline} />
                              </IonButton>
                            </div>
                            {props.imageryIndex !== 'RGB' &&
                            currentRaster?.heterogeneity != null ? (
                              <div className={styles.heterogeneity}>
                                {t('plot:heterogeneity_index')}:{' '}
                                {currentRaster.heterogeneity}
                              </div>
                            ) : null}
                          </>
                        )}
                      </IonLabel>
                    </IonItem>
                  );
                })}
              </IonList>
            </section>

            <section className={`${styles.section} ${styles.legendSection}`}>
              {props.imageryIndex !== 'RGB' && props.colorbar ? (
                <ColorScale
                  colorGradient={props.colorbar.color}
                  max={props.colorbar.max}
                  min={props.colorbar.min}
                  mobile
                  stops={props.colorbar.stops}
                />
              ) : null}
              {props.imageryIndex === 'RGB' ? (
                <p className={styles.rgbHint}>{t('plot:no_colorbar')}</p>
              ) : null}
            </section>
          </>
        ) : null}

        <MergeAlert
          isOpen={mergeDialogOpened}
          onCancel={() => mergeDialogHandlers.close()}
          onConfirm={handleMergePlot}
        />
      </div>
    </ModalPage>
  );
}

export default SatelliteMapModal;
